import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { publicApi } from '../api';
import { useFetch } from '../useFetch';

const AUTO_MS = 6000;

// Absolute URL helper (handle /api/uploads paths)
const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return src.startsWith('/') ? `${base}${src}` : `${base}/${src}`;
};

export default function SponsorBannerCarousel({ citySlug }) {
  const { data: banners } = useFetch(
    `banners:${citySlug || 'all'}`,
    () => publicApi.banners(citySlug || undefined),
    [citySlug]
  );
  const [index, setIndex] = useState(0);
  const [hover, setHover] = useState(false);
  const timerRef = useRef(null);

  const active = banners || [];
  const count = active.length;

  const goTo = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const next = useCallback(() => setIndex(i => (i + 1) % Math.max(count, 1)), [count]);
  const prev = useCallback(() => setIndex(i => (i - 1 + Math.max(count, 1)) % Math.max(count, 1)), [count]);

  useEffect(() => {
    if (count < 2 || hover) return;
    timerRef.current && clearInterval(timerRef.current);
    timerRef.current = setInterval(next, AUTO_MS);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [count, hover, next]);

  if (!count) return null;
  const cur = active[index];

  const Inner = (
    <>
      <img
        src={resolveImg(cur.image)}
        alt={cur.title || 'Sponsor banner'}
        className="w-full h-full object-cover transition-opacity duration-500"
        loading="lazy"
      />
      {/* Overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent pointer-events-none" />

      {/* Sponsor label (top-right) */}
      <div className="absolute top-3 right-3 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest text-black shadow-md"
           style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>
        Sponsor
      </div>

      {/* Title (if set) */}
      {cur.title && (
        <div className="absolute inset-0 flex items-center pl-6 md:pl-10 pointer-events-none">
          <h3 className="text-white text-xl md:text-3xl font-bold max-w-md drop-shadow-lg"
              style={{ textShadow: '0 3px 15px rgba(0,0,0,0.7)' }}>
            {cur.title}
          </h3>
        </div>
      )}
    </>
  );

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden shadow-2xl border border-[color:var(--yup-primary)]/25 bg-black/40"
      style={{ aspectRatio: '4 / 1', minHeight: '150px' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid="sponsor-banner-carousel"
    >
      {cur.link_url ? (
        <a
          href={cur.link_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={() => publicApi.bannerClick(cur.id)}
          className="block absolute inset-0"
          data-testid={`sponsor-banner-link-${cur.id}`}
        >
          {Inner}
        </a>
      ) : (
        <div className="absolute inset-0">{Inner}</div>
      )}

      {/* Navigation arrows (only if 2+) */}
      {count > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Precedente"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition"
            data-testid="banner-prev"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            aria-label="Successivo"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition"
            data-testid="banner-next"
          >
            <ChevronRight size={20} />
          </button>
          {/* Dots */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid="banner-dots">
            {active.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Vai al banner ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-6 bg-[color:var(--yup-primary)]' : 'w-1.5 bg-white/50 hover:bg-white/80'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
